import { useMemo, useState } from "react";
import { Lock } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { useTabletKiosk } from "@/hooks/useTabletKiosk";

interface TabletKioskUnlockDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenAdmin: () => void;
}

export function TabletKioskUnlockDialog({
  open,
  onOpenChange,
  onOpenAdmin,
}: TabletKioskUnlockDialogProps) {
  const kiosk = useTabletKiosk();
  const [pin, setPin] = useState("");
  const [unlocked, setUnlocked] = useState(false);

  const pinLabel = useMemo(() => (kiosk.hasPin ? "PIN" : "PIN não configurado"), [kiosk.hasPin]);

  const handleUnlock = () => {
    const result = kiosk.verify(pin);
    if (!result.ok) {
      toast.error(result.message);
      return;
    }
    setUnlocked(true);
    toast.success("Desbloqueado");
  };

  const handleOpenAdmin = () => {
    onOpenChange(false);
    setPin("");
    setUnlocked(false);
    onOpenAdmin();
  };

  const handleDisableKiosk = () => {
    kiosk.disable();
    onOpenChange(false);
    setPin("");
    setUnlocked(false);
    toast.success("Modo Kiosk desativado");
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) {
          setPin("");
          setUnlocked(false);
        }
      }}
    >
      <DialogContent className="max-w-sm bg-slate-900 border-slate-800">
        <DialogHeader>
          <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/20 flex items-center justify-center">
            <Lock className="h-8 w-8 text-primary" />
          </div>
          <DialogTitle className="text-white text-xl text-center">
            Modo Kiosk
          </DialogTitle>
        </DialogHeader>

        {!kiosk.hasPin && (
          <Alert>
            <AlertDescription>
              Este tablet está em modo Kiosk, mas o PIN não está configurado. Desative o modo na tela de setup e configure novamente.
            </AlertDescription>
          </Alert>
        )}

        {!unlocked ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleUnlock();
            }}
            className="space-y-4"
          >
            <Input
              type="password"
              inputMode="numeric"
              placeholder={pinLabel}
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              className="text-center text-2xl tracking-[0.35em] font-mono bg-slate-900/50 border-slate-600 text-white h-14"
              disabled={!kiosk.hasPin}
              autoFocus
            />

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                className="flex-1 border-slate-600"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" className="flex-1" disabled={!kiosk.hasPin}>
                Desbloquear
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-3">
            <Button className="w-full" onClick={handleOpenAdmin}>
              Alterar Mesa / Configurações
            </Button>
            <Button
              variant="outline"
              className="w-full border-slate-600"
              onClick={handleDisableKiosk}
            >
              Desativar Modo Kiosk
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
